import React, { useState, useEffect } from 'react';
import MainLayout from '../components/MainLayout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Shield, Building2, Mail, Lock, X, Eye, EyeOff, Loader2, Save, KeyRound,
  CheckCircle2, AlertTriangle, Phone, Briefcase, Calendar, UserCheck,
  MapPin, Sparkles, ChevronRight, BadgeCheck, User, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Profile = () => {
  const { user } = useAuth();
  const [empDetails, setEmpDetails] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Password Modal States
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchProfileDetails = async () => {
      setLoadingProfile(true);
      try {
        const res = await axios.get(`${API_URL}/ess/profile`, { withCredentials: true });
        if (isMounted && res.data?.employee) {
          setEmpDetails(res.data.employee);
        }
      } catch (err) {
        // Fall back gracefully to user context
      } finally {
        if (isMounted) setLoadingProfile(false);
      }
    };
    fetchProfileDetails();
    return () => {
      isMounted = false;
    };
  }, []);

  const openPwdModal = () => {
    setPwdForm({ current_password: '', new_password: '', confirm_password: '' });
    setPwdError('');
    setShowPwdModal(true);
  };

  const handleChangePassword = async () => {
    const { current_password, new_password, confirm_password } = pwdForm;
    if (!current_password || !new_password) { setPwdError('All fields are required'); return; }
    if (new_password.length < 8) { setPwdError('New password must be at least 8 characters'); return; }
    if (new_password !== confirm_password) { setPwdError('New passwords do not match'); return; }
    if (new_password === current_password) { setPwdError('New password must differ from the current one'); return; }
    setPwdError('');
    setPwdSaving(true);
    try {
      await axios.post(
        `${API_URL}/auth/change-password`,
        { current_password, new_password },
        { withCredentials: true }
      );
      toast.success('Password updated. Use it on your next login.');
      setShowPwdModal(false);
    } catch (err) {
      setPwdError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwdSaving(false);
    }
  };

  // Extract resolved values with single-source-of-truth fallback
  const empCode = user?.employee_code || user?.emp_code || user?.employeeCode || empDetails?.employment_details?.employee_code || '';
  const fullName = user?.name || (empDetails?.personal_details ? `${empDetails.personal_details.first_name || ''} ${empDetails.personal_details.last_name || ''}`.trim() : '') || 'Employee Profile';
  const userEmail = user?.email || empDetails?.employment_details?.work_email || empDetails?.personal_details?.personal_email || '—';
  const designation = empDetails?.employment_details?.designation || user?.designation || 'Team Member';
  const department = empDetails?.employment_details?.department || user?.department || 'Operations';
  const companyName = user?.company_name || (user?.company_id ? 'Assigned' : '—');
  const userRole = user?.role_name || user?.role?.replace(/_/g, ' ') || 'Employee';
  const mobile = empDetails?.personal_details?.mobile || empDetails?.personal_details?.phone || user?.phone || '—';
  const empType = empDetails?.employment_details?.employment_type || empDetails?.employment_details?.worker_type || 'Full-Time';
  const dateOfJoining = empDetails?.employment_details?.date_of_joining || empDetails?.employment_details?.doj || '—';
  const reportingManager = empDetails?.employment_details?.reporting_manager_name || empDetails?.employment_details?.reporting_manager || '—';
  const empStatus = empDetails?.employment_details?.employment_status || user?.status || 'Active';
  const location = empDetails?.employment_details?.location || 'Headquarters';

  // Calculate Profile Completion Score
  const completionFields = [
    fullName !== 'Employee Profile',
    Boolean(empCode),
    userEmail !== '—',
    department !== 'Operations' && Boolean(department),
    designation !== 'Team Member' && Boolean(designation),
    mobile !== '—',
    companyName !== '—',
    dateOfJoining !== '—',
  ];
  const filledCount = completionFields.filter(Boolean).length;
  const completionPct = Math.round((filledCount / completionFields.length) * 100);

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto space-y-6 pb-12" data-testid="profile-page">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-200 pb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
              My Profile
              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-[#0052FF] border border-blue-200">
                <BadgeCheck className="w-3.5 h-3.5" /> Enterprise Account
              </span>
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Manage your identity, professional employment details, and account security settings.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <Button
              onClick={openPwdModal}
              data-testid="change-password-button"
              className="bg-[#0052FF] hover:bg-[#0043D1] text-white text-sm font-medium shadow-sm transition-all flex items-center gap-2"
            >
              <Lock className="w-4 h-4" /> Change Password
            </Button>
          </div>
        </div>

        {/* 2. Account Security Alert (Force Password Change Notice) */}
        {user?.force_password_change ? (
          <div
            className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5 shadow-sm transition-all flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
            data-testid="force-change-banner"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0 mt-0.5">
                <KeyRound className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <h3 className="text-base font-bold text-amber-950 flex items-center gap-2">
                  Password Change Required
                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-200 text-amber-900">Immediate Action Needed</span>
                </h3>
                <p className="text-sm text-amber-800 mt-0.5">
                  Your administrator assigned a temporary password for this account. Please update it now to secure your identity.
                </p>
              </div>
            </div>
            <Button
              onClick={openPwdModal}
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold shadow-md px-5 py-2.5 text-sm shrink-0 w-full md:w-auto"
              data-testid="force-change-cta"
            >
              Change Now
            </Button>
          </div>
        ) : (
          <div className="bg-emerald-50/80 border border-emerald-200 rounded-xl p-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-emerald-950">Password Secure</p>
                <p className="text-xs text-emerald-700">Your account password is up to date and active.</p>
              </div>
            </div>
            <span className="text-xs font-medium text-emerald-800 bg-emerald-100/80 px-2.5 py-1 rounded-full border border-emerald-200">
              Verified Status: OK
            </span>
          </div>
        )}

        {/* 1. Profile Header Hero Card */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-50/50 via-slate-50/20 to-transparent rounded-full -mr-20 -mt-20 pointer-events-none" />

          {loadingProfile ? (
            <div className="animate-pulse flex flex-col md:flex-row items-center md:items-start gap-6">
              <div className="w-24 h-24 rounded-full bg-slate-200 shrink-0" />
              <div className="flex-1 space-y-3 w-full">
                <div className="h-6 bg-slate-200 rounded w-1/3" />
                <div className="h-4 bg-slate-200 rounded w-1/4" />
                <div className="h-16 bg-slate-100 rounded-lg w-full mt-4" />
              </div>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6 relative z-10">
              {/* Profile Avatar */}
              <div className="relative group">
                <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-[#0052FF] to-[#0038B8] flex items-center justify-center text-white text-3xl sm:text-4xl font-bold shadow-md border-4 border-white shrink-0">
                  {fullName.charAt(0)?.toUpperCase() || 'E'}
                </div>
                <span className="absolute bottom-1 right-1 w-4 h-4 bg-emerald-500 border-2 border-white rounded-full" title="Active Account" />
              </div>

              {/* Identity & Badges */}
              <div className="flex-1 text-center md:text-left">
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                    {fullName}
                  </h2>

                  <div className="flex items-center justify-center md:justify-start gap-2 flex-wrap">
                    {empCode && (
                      <span
                        className="inline-flex items-center text-xs font-mono font-bold px-2.5 py-1 rounded-md bg-blue-50 text-[#0052FF] border border-blue-200/80 shadow-2xs"
                        data-testid="profile-employee-code"
                      >
                        {empCode}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {empStatus}
                    </span>
                  </div>
                </div>

                <p className="text-sm font-medium text-slate-600 mt-1 flex items-center justify-center md:justify-start gap-2 flex-wrap">
                  <span>{designation}</span>
                  <span className="text-slate-300">•</span>
                  <span>{department}</span>
                  <span className="text-slate-300">•</span>
                  <span className="text-slate-500 font-normal">{companyName}</span>
                </p>

                {/* Profile Completion Indicator */}
                <div className="mt-5 pt-4 border-t border-slate-100 max-w-xl">
                  <div className="flex items-center justify-between text-xs font-semibold text-slate-600 mb-1.5">
                    <span className="flex items-center gap-1.5 text-slate-700">
                      <Sparkles className="w-3.5 h-3.5 text-[#0052FF]" /> Profile Completion
                    </span>
                    <span className="text-[#0052FF]">{completionPct}%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#0052FF] to-blue-400 rounded-full transition-all duration-500"
                      style={{ width: `${completionPct}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 3. Profile Information Section & Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Personal Information Card */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all space-y-4">
            <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100">
              <div className="p-2 bg-blue-50 text-[#0052FF] rounded-lg">
                <User className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">Personal Information</h3>
                <p className="text-xs text-slate-500">Identity and contact details</p>
              </div>
            </div>

            <div className="space-y-3.5 pt-1 text-sm">
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">Full Name</span>
                <p className="font-semibold text-slate-900 mt-0.5">{fullName}</p>
              </div>

              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">Employee Code</span>
                <p className="font-mono font-semibold text-[#0052FF] mt-0.5">{empCode || '—'}</p>
              </div>

              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">Work Email</span>
                <p className="font-medium text-slate-900 mt-0.5 flex items-center gap-1.5 break-all">
                  <Mail className="w-4 h-4 text-slate-400 shrink-0" /> {userEmail}
                </p>
              </div>

              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">Mobile / Contact</span>
                <p className="font-medium text-slate-900 mt-0.5 flex items-center gap-1.5">
                  <Phone className="w-4 h-4 text-slate-400 shrink-0" /> {mobile}
                </p>
              </div>
            </div>
          </div>

          {/* Employment Information Card */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all space-y-4 lg:col-span-2">
            <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100">
              <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
                <Briefcase className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">Employment Information</h3>
                <p className="text-xs text-slate-500">Role, department, and organizational hierarchy</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm pt-1">
              <div className="p-3 bg-slate-50/70 border border-slate-100 rounded-lg">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">Designation</span>
                <p className="font-bold text-slate-900 mt-1 flex items-center gap-1.5">
                  <Briefcase className="w-4 h-4 text-purple-500" /> {designation}
                </p>
              </div>

              <div className="p-3 bg-slate-50/70 border border-slate-100 rounded-lg">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">Department</span>
                <p className="font-bold text-slate-900 mt-1 flex items-center gap-1.5">
                  <Building2 className="w-4 h-4 text-blue-500" /> {department}
                </p>
              </div>

              <div className="p-3 bg-slate-50/70 border border-slate-100 rounded-lg">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">Company</span>
                <p className="font-bold text-slate-900 mt-1 flex items-center gap-1.5">
                  <Building2 className="w-4 h-4 text-emerald-500" /> {companyName}
                </p>
              </div>

              <div className="p-3 bg-slate-50/70 border border-slate-100 rounded-lg">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">Employment Type</span>
                <p className="font-bold text-slate-900 mt-1 flex items-center gap-1.5">
                  <UserCheck className="w-4 h-4 text-amber-500" /> {empType}
                </p>
              </div>

              <div className="p-3 bg-slate-50/70 border border-slate-100 rounded-lg">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">Date of Joining</span>
                <p className="font-bold text-slate-900 mt-1 flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-indigo-500" /> {dateOfJoining}
                </p>
              </div>

              <div className="p-3 bg-slate-50/70 border border-slate-100 rounded-lg">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">Reporting Manager</span>
                <p className="font-bold text-slate-900 mt-1 flex items-center gap-1.5">
                  <User className="w-4 h-4 text-slate-500" /> {reportingManager}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 5. Account Status Badges Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs flex items-center gap-3">
            <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-lg shrink-0">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Account Status</p>
              <span className="inline-block mt-0.5 px-2 py-0.5 rounded text-xs font-bold bg-emerald-100 text-emerald-800">
                {empStatus}
              </span>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs flex items-center gap-3">
            <div className="p-2.5 bg-amber-50 text-amber-600 rounded-lg shrink-0">
              <Lock className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Password Status</p>
              <span className={`inline-block mt-0.5 px-2 py-0.5 rounded text-xs font-bold ${user?.force_password_change ? 'bg-amber-100 text-amber-900' : 'bg-emerald-100 text-emerald-800'}`}>
                {user?.force_password_change ? 'Change Required' : 'Secure'}
              </span>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs flex items-center gap-3">
            <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg shrink-0">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Company Scope</p>
              <span className="inline-block mt-0.5 px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-800">
                {user?.company_id ? 'Assigned' : 'Unassigned'}
              </span>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs flex items-center gap-3">
            <div className="p-2.5 bg-purple-50 text-purple-600 rounded-lg shrink-0">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Access Role</p>
              <span className="inline-block mt-0.5 px-2 py-0.5 rounded text-xs font-bold bg-purple-100 text-purple-800 capitalize">
                {userRole}
              </span>
            </div>
          </div>
        </div>

        {/* 4. Upgraded Account Actions Section */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
          <div className="border-b border-slate-100 pb-3">
            <h3 className="text-lg font-bold text-slate-900">Account Security & Actions</h3>
            <p className="text-xs text-slate-500">Manage security credentials and security controls</p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {/* Action: Change Password */}
            <button
              onClick={openPwdModal}
              data-testid="change-password-button"
              className="group w-full text-left p-4 border-2 border-slate-100 hover:border-blue-200 rounded-xl hover:bg-blue-50/30 transition-all flex items-center justify-between"
            >
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-lg bg-blue-50 text-[#0052FF] group-hover:bg-[#0052FF] group-hover:text-white transition-all flex items-center justify-center shrink-0">
                  <Lock className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-sm flex items-center gap-2">
                    Change Password
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">Update your primary login security password</p>
                </div>
              </div>
              <span className="text-xs font-bold text-[#0052FF] flex items-center gap-1 group-hover:translate-x-0.5 transition-transform shrink-0">
                Open <ChevronRight className="w-4 h-4" />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* ======= CHANGE PASSWORD MODAL ======= */}
      {showPwdModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4" data-testid="change-password-modal">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden border border-slate-100">
            <div className="flex items-center justify-between p-5 border-b border-slate-200 bg-slate-50/80">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#0052FF] flex items-center justify-center text-white shadow-sm">
                  <Lock className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Change Password</h2>
                  <p className="text-xs text-slate-500">Enter your current password and choose a new one</p>
                </div>
              </div>
              <button
                onClick={() => setShowPwdModal(false)}
                data-testid="close-change-password"
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {pwdError && (
                <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2" data-testid="change-password-error">
                  <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                  <span>{pwdError}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Current Password *</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    data-testid="current-password-input"
                    type={showCurrent ? 'text' : 'password'}
                    value={pwdForm.current_password}
                    onChange={e => setPwdForm({ ...pwdForm, current_password: e.target.value })}
                    placeholder="Your current / temporary password"
                    className="w-full h-11 pl-10 pr-11 bg-white border-2 border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:border-[#0052FF] focus:ring-2 focus:ring-[#0052FF]/10 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent(v => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">New Password *</label>
                <div className="relative">
                  <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    data-testid="new-password-input"
                    type={showNew ? 'text' : 'password'}
                    value={pwdForm.new_password}
                    onChange={e => setPwdForm({ ...pwdForm, new_password: e.target.value })}
                    placeholder="At least 8 characters"
                    className="w-full h-11 pl-10 pr-11 bg-white border-2 border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:border-[#0052FF] focus:ring-2 focus:ring-[#0052FF]/10 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(v => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Confirm New Password *</label>
                <div className="relative">
                  <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    data-testid="confirm-password-input"
                    type={showNew ? 'text' : 'password'}
                    value={pwdForm.confirm_password}
                    onChange={e => setPwdForm({ ...pwdForm, confirm_password: e.target.value })}
                    placeholder="Re-type new password"
                    className="w-full h-11 pl-10 pr-4 bg-white border-2 border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:border-[#0052FF] focus:ring-2 focus:ring-[#0052FF]/10 transition-all"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-200 bg-slate-50/80">
              <Button
                variant="outline"
                onClick={() => setShowPwdModal(false)}
                className="px-5 border-slate-300 hover:bg-slate-100 rounded-xl text-slate-700 text-sm font-semibold"
              >
                Cancel
              </Button>
              <Button
                onClick={handleChangePassword}
                disabled={pwdSaving}
                data-testid="submit-change-password"
                className="bg-[#0052FF] hover:bg-[#0043D1] text-white px-5 rounded-xl font-semibold text-sm shadow-md transition-all"
              >
                {pwdSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Update Password
              </Button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Profile;
